/**
 * Ícones inline SVG — estilo stroke v5 (decisão de design v5, Sessão VI).
 *
 * Geometria alinhada com v5-bottomnav-amostras.html.
 * Todos seguem: viewBox 24, fill=none, stroke=currentColor,
 * stroke-width 1.8, linecap/linejoin round.
 *
 * Inline para evitar dependência extra e manter bundle pequeno.
 * Todos aceitam `size` e `color` (default 24 e 'currentColor').
 */

interface IconProps {
  size?: number | string
  color?: string
  className?: string
  style?: React.CSSProperties
}

const iconDefaults = (size: IconProps['size'] = 24, color = 'currentColor') => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none' as const,
  stroke: color,
  strokeWidth: 1.8,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  xmlns: 'http://www.w3.org/2000/svg',
})

// ---------- Folha (botânica) ----------
export function LeafIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M20.5 3.5c0 9-5 15-12 15a4 4 0 0 1-4-4c0-7 6-12 16-11z" />
      <path d="M4 21l8-8" />
    </svg>
  )
}

// ---------- Câmera ----------
export function CameraIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M9 4l-1.5 2.5H5a2 2 0 0 0-2 2V18a2 2 0 0 0 2 2H19a2 2 0 0 0 2-2V8.5a2 2 0 0 0-2-2H16.5L15 4H9z" />
      <circle cx="12" cy="13" r="3.5" />
    </svg>
  )
}

// ---------- Envelope ----------
export function EnvelopeIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <rect x="3" y="5" width="18" height="14" rx="2" />
      <path d="M3 6l9 7 9-7" />
    </svg>
  )
}

// ---------- Cadeado ----------
export function LockIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <rect x="4" y="11" width="16" height="10" rx="2" />
      <path d="M8 11V7a4 4 0 0 1 8 0v4" />
    </svg>
  )
}

// ---------- Check ----------
export function CheckIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M5 12l4 4L19 6" />
    </svg>
  )
}

// ---------- Aviso (triângulo !) ----------
export function WarningIcon({ size, color, className, style }: IconProps) {
  const dotColor = color || 'currentColor'
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M12 3L2 20h20L12 3z" />
      <path d="M12 10v4" />
      <circle cx="12" cy="17" r="1" fill={dotColor} stroke="none" />
    </svg>
  )
}

// ---------- Telefone ----------
export function PhoneIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M5.5 4.5l3-0.5 2 4-2 1.5c0.5 3 2.5 5 5.5 5.5l1.5-2 4 2-0.5 3a2 2 0 0 1-2 1.5c-7 0-13-6-13-13a2 2 0 0 1 1.5-2z" />
    </svg>
  )
}

// ---------- Mapa (pin) ----------
export function MapPinIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M12 22s-7-7-7-13a7 7 0 0 1 14 0c0 6-7 13-7 13z" />
      <circle cx="12" cy="9" r="2.5" />
    </svg>
  )
}

// ---------- Seta esquerda ----------
export function ArrowLeftIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M19 12H5" />
      <path d="M12 19l-7-7 7-7" />
    </svg>
  )
}

// ---------- Spinner (carregando — rotação aplicada pelo consumidor via CSS) ----------
export function SpinnerIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={`v5-spin${className ? ' ' + className : ''}`} style={style}>
      <path d="M21 12a9 9 0 1 1-9-9" />
    </svg>
  )
}

// ---------- Home ----------
export function HomeIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M3 9l9-6 9 6v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V9z" />
      <path d="M9 21v-9h6v9" />
    </svg>
  )
}

// ---------- Relógio ----------
export function ClockIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <circle cx="12" cy="12" r="8.5" />
      <path d="M12 7.5V12l3 2" />
    </svg>
  )
}

// ---------- Usuário ----------
export function UserIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <circle cx="12" cy="8" r="3.5" />
      <path d="M5 20c0-3.5 3.1-6 7-6s7 2.5 7 6" />
    </svg>
  )
}

// ---------- X (fechar) ----------
export function XIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M18 6L6 18" />
      <path d="M6 6l12 12" />
    </svg>
  )
}

// ---------- Busca ----------
export function SearchIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <circle cx="11" cy="11" r="6" />
      <path d="M20 20l-4.5-4.5" />
    </svg>
  )
}

// ---------- Upload ----------
export function UploadIcon({ size, color, className, style }: IconProps) {
  return (
    <svg {...iconDefaults(size, color)} className={className} style={style}>
      <path d="M12 16V4" />
      <path d="M7 9l5-5 5 5" />
      <path d="M3 17v2a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-2" />
    </svg>
  )
}
